import { useEffect, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import type { Review } from '@vibesites/schema';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://lirp.cdn-website.com/ff8e9a4b/dms3rep/multi/opt/The-collection-at-highland-bridge-023-1920w.png',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Residents enjoying time together at home',
    },
  },
};

const FALLBACK_REVIEWS: { text: string; name: string }[] = [
  {
    text: 'What a wonderful experience from start to finish. The leasing staff are warm and caring making every effort to help you find your perfect apartment.',
    name: 'Joe M.',
  },
  {
    text: 'I love this community because the environment here is beautiful and amazing view of the water right from my apartment.',
    name: 'Jessica S.',
  },
  {
    text: 'Everyone is so helpful, you can get almost all questions about the apartment asked without having to waste your time. It helped me take a few apartments out of the running.',
    name: 'Mason R.',
  },
  {
    text: 'The website made it super easy to compare different apartments, and we were able to ultimately pick an apartment for my brother to go visit in person.',
    name: 'Tara W.',
  },
];

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function abbreviateName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || name;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function PersonAvatar() {
  return (
    <div className="rr-avatar" aria-hidden="true">
      <svg viewBox="0 0 40 40" className="w-7 h-7" fill="none">
        <circle cx="20" cy="14" r="7" fill="currentColor" />
        <path d="M8 34c0-7.18 5.37-13 12-13s12 5.82 12 13" fill="currentColor" />
      </svg>
    </div>
  );
}

export function ResidentReviews() {
  const bg = useSlotGroup('residentreviews.bg_image', bgImageDef);
  const reviews = useCollectionBinding<Review>('residentreviews.items', {
    collection: 'reviews',
    defaultLimit: 4,
  });

  const displayItems =
    reviews && reviews.length > 0
      ? reviews.slice(0, 4).map((review) => ({
          text: hasValue(review.text) ? stripHtml(review.text) : '',
          name: hasValue(review.name) ? abbreviateName(review.name) : '',
        }))
      : FALLBACK_REVIEWS;

  const bgUrl =
    bg.image ||
    'https://lirp.cdn-website.com/ff8e9a4b/dms3rep/multi/opt/The-collection-at-highland-bridge-023-1920w.png';

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const cardsRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 100 });
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const isMobile = window.matchMedia('(max-width: 767px)');

    let raf = 0;
    const update = () => {
      raf = 0;
      const top = section.getBoundingClientRect().top;
      if (isMobile.matches) {
        // Pin a viewport-tall plate so a landscape photo is never stretched
        // across the stacked-card section height (which looks blurry on retina).
        imgWrap.style.transform = `translate3d(0, ${-top}px, 0)`;
        return;
      }
      if (reduceMotion.matches) {
        imgWrap.style.transform = '';
        return;
      }
      imgWrap.style.transform = `translate3d(0, ${-top * 0.4}px, 0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    let scrollRoot: HTMLElement | Window = window;
    let node: HTMLElement | null = section.parentElement;
    while (node && node !== document.body) {
      const { overflowY } = getComputedStyle(node);
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollRoot = node;
        break;
      }
      node = node.parentElement;
    }

    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    isMobile.addEventListener('change', onScroll);
    update();
    return () => {
      scrollRoot.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      isMobile.removeEventListener('change', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={sectionRef} className="rr-section">
      <EditableSlotGroup groupPath="residentreviews.bg_image" definition={bgImageDef}>
        <div ref={imgWrapRef} className="rr-parallax-wrap">
          <VibesitesImage
            url={bgUrl}
            alt={bg.alt || 'Residents enjoying time together at home'}
            width={1920}
            sizes="(max-width: 767px) 900px, 100vw"
            className="rr-bg-image"
          />
        </div>
      </EditableSlotGroup>
      <div className="rr-bg-overlay" aria-hidden="true" />

      <div className="rr-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div ref={headingRef} className="reveal-fade-up text-center mb-12 md:mb-16">
          <RichTextContent
            path="residentreviews.title"
            label="Title"
            defaultValue="<p>Resident Reviews</p>"
            as="h2"
            className="rr-title"
          />
        </div>

        <EditableCollectionBinding bindingPath="residentreviews.items" label="Reviews">
          <div
            ref={cardsRef}
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-14"
          >
            {displayItems.map((item, index) => (
              <article key={index} data-reveal className="reveal-fade-up rr-card">
                <div className="rr-card-body">
                  {item.text ? (
                    <div className="rr-quote-scroll">
                      <p className="rr-quote">&ldquo;{item.text}&rdquo;</p>
                    </div>
                  ) : null}
                </div>
                <div className="rr-card-footer">
                  <PersonAvatar />
                  {item.name ? <p className="rr-name">{item.name.toUpperCase()}</p> : null}
                </div>
              </article>
            ))}
          </div>
        </EditableCollectionBinding>
      </div>

      <style>{`
        .rr-section {
          position: relative;
          overflow: hidden;
        }

        .rr-parallax-wrap {
          position: absolute;
          inset-inline: 0;
          top: -30%;
          height: 160%;
          z-index: 0;
          will-change: transform;
        }

        .rr-bg-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          filter: grayscale(1) brightness(1.2);
        }

        /* Viewport-tall plate: cards stack on small screens and a 160% section
           cover would upscale a landscape photo across the full height. */
        @media (max-width: 767px) {
          .rr-parallax-wrap {
            top: 0;
            height: 100vh;
            height: 100lvh;
          }
        }

        .rr-bg-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: #fff;
          opacity: 0.62;
          pointer-events: none;
        }

        .rr-content {
          position: relative;
          z-index: 2;
        }

        @media (prefers-reduced-motion: reduce) and (min-width: 768px) {
          .rr-parallax-wrap {
            transform: none !important;
            top: 0;
            height: 100%;
          }
        }

        .rr-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 1.875rem;
          font-weight: 400;
          line-height: 1.2;
          color: var(--vibe-color-primary, #B28C59);
          margin: 0;
          letter-spacing: 0.01em;
        }
        @media (min-width: 768px) {
          .rr-title {
            font-size: 2.25rem;
          }
        }
        .rr-title p {
          margin: 0;
        }

        .rr-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          height: 100%;
        }

        .rr-card-body {
          position: relative;
          width: 100%;
          background: #fff;
          box-shadow: 0 8px 28px rgba(52, 46, 31, 0.12);
          padding: 1.5rem 1.15rem 2.85rem;
          height: 15.5rem;
          min-height: 15.5rem;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .rr-quote-scroll {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
          padding: 0 0.2rem;
          scrollbar-gutter: stable;
          scrollbar-width: thin;
          scrollbar-color: rgba(52, 46, 31, 0.28) transparent;
        }

        .rr-quote-scroll::-webkit-scrollbar {
          width: 5px;
        }
        .rr-quote-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .rr-quote-scroll::-webkit-scrollbar-thumb {
          background: rgba(52, 46, 31, 0.28);
          border-radius: 999px;
        }

        .rr-quote {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.55;
          color: var(--vibe-color-text, #342E1F);
          margin: 0;
        }

        .rr-card-footer {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: -1.75rem;
          position: relative;
          z-index: 1;
        }

        .rr-avatar {
          width: 3.5rem;
          height: 3.5rem;
          border-radius: 9999px;
          background: var(--vibe-color-secondary, #3E441D);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid #fff;
          box-shadow: 0 4px 12px rgba(52, 46, 31, 0.18);
        }

        .rr-name {
          font-family: var(--vibe-font-body);
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text, #342E1F);
          margin: 0.65rem 0 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentReviews',
  displayName: 'Resident Reviews',
  tokens: {
    'residentreviews.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Resident Reviews</p>',
    },
  },
  slotGroups: {
    'residentreviews.bg_image': bgImageDef,
  },
  collectionBindings: {
    'residentreviews.items': {
      label: 'Reviews',
      description: 'Featured resident reviews shown as quote cards',
      collection: 'reviews',
      defaultLimit: 4,
    },
  },
};

(ResidentReviews as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ResidentReviews;
